import type { WorkflowTemplate } from './workflow';

/**
 * Workflow templates — the pre-built deliverables.
 *
 * Each is an ordinary workflow with its inputs declared and unfilled, so a
 * template renders through exactly the same path as an agent-authored
 * workflow. That means the gallery works with no LLM involved at all, and a
 * bad agent turn can never break the demo.
 *
 * The catalogue mirrors the two QuickSight products this app replaces:
 *   · Content Valuation — Leaderboard, Title Profile, Compare Titles
 *   · Streaming Metrics — Leaderboard, Platform Insights, Head-to-Head,
 *     Compare Platforms, Catalog Insights
 *
 * Every block here was checked against a live response. Where a template needs
 * a platform-shaped row (subscribers/ARPU/churn) rather than a revenue-shaped
 * one, its blocks carry an explicit `metric` so the renderer reads the right
 * field instead of guessing.
 *
 * ── The deliverable shape ────────────────────────────────────────────────
 *
 * Every template follows the structure `pattern-synthesis.md` found in 100% of
 * real Parrot deliverables, rather than being a list of charts:
 *
 *   1. A cover identifying the subject, where the subject is a single thing.
 *   2. Sections, each introduced by a heading, each answering one question.
 *   3. A computed `note` closing each section — a read derived from the same
 *      rows the charts draw.
 *
 * The notes are the part that matters. "69% of revenue is earned outside UCAN"
 * is specific and cannot be wrong for a given subject, because nothing about it
 * was authored — the sentence is assembled from the response. A hand-written
 * sentence in a template is a guess that happens to be printed next to real
 * figures, which is worse than no sentence at all.
 *
 * Available reads, by row shape (see `NoteBlock`):
 *   · revenue rows — summary · geography · role · platforms · comps ·
 *     concentration · genres
 *   · platform rows — market · churn
 *   · platform history — growth · footprint
 *
 * A note whose `insight` doesn't match the shape of its source renders a
 * confident zero, so the pairing below is deliberate in every case.
 */
export const WORKFLOW_TEMPLATES: WorkflowTemplate[] = [
  // ─── Content Valuation ───────────────────────────────────────────────────

  {
    id: 'title-valuation',
    name: 'Title Valuation',
    description:
      'What is one title worth to a platform? Revenue to date, how it splits between winning and keeping subscribers, where it earns, and how it stands against comparable titles.',
    category: 'Content Valuation',
    inputs: [
      { key: 'titleId', label: 'Title', type: 'title', required: true, placeholder: 'Search for a title…' },
    ],
    defaultScope: { region: '', timePeriod: 'all' },
    /*
     * Structured as a deliverable, against the four sections
     * `pattern-synthesis.md` finds in 100% of Parrot valuations: core
     * valuation, geographic decomposition, comparable titles, platform
     * context. Comps and rank-in-context were missing from every template
     * despite being universal.
     */
    blocks: [
      {
        id: 'tv-banner',
        type: 'banner',
        title: 'Subject',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}' },
      },

      // ─── Headline value ──────────────────────────────────────────────────
      {
        id: 'tv-h-value',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Valuation summary',
        options: { variant: 'heading' },
      },
      {
        id: 'tv-kpis',
        type: 'kpis',
        title: 'Headline figures',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { rankSource: '/streaming-economics/titles/{{titleId}}/rankings' },
      },
      {
        id: 'tv-read-value',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { insight: 'summary' },
      },
      {
        id: 'tv-timeseries',
        type: 'chart',
        title: 'Revenue over time',
        subtitle: 'Acquisition and retention revenue by quarter',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue/time-series' },
        options: { chartType: 'area' },
      },

      // ─── Geography ───────────────────────────────────────────────────────
      {
        id: 'tv-h-geo',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Where the value is earned',
        options: { variant: 'heading' },
      },
      {
        id: 'tv-regions',
        type: 'chart',
        title: 'Revenue by region',
        subtitle: 'UCAN, EMEA, APAC and LATAM',
        layout: 'half',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { chartType: 'donut', groupBy: 'region' },
      },
      {
        id: 'tv-platforms',
        type: 'chart',
        title: 'Revenue by platform',
        subtitle: 'Across every platform carrying the title',
        layout: 'half',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { chartType: 'bar', groupBy: 'platform' },
      },
      {
        id: 'tv-read-geo',
        type: 'note',
        title: 'The read',
        layout: 'half',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { insight: 'geography' },
      },
      {
        id: 'tv-read-platforms',
        type: 'note',
        title: 'The read',
        layout: 'half',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { insight: 'platforms' },
      },

      // ─── What the title does ─────────────────────────────────────────────
      {
        id: 'tv-h-role',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'What the title does',
        options: { variant: 'heading' },
      },
      {
        id: 'tv-split',
        type: 'split',
        title: 'Acquisition vs retention',
        subtitle: 'Did this title win subscribers, or keep them?',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
      },
      {
        id: 'tv-read-role',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { insight: 'role' },
      },

      // ─── Comparable titles ───────────────────────────────────────────────
      {
        id: 'tv-h-comps',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'How it compares',
        options: { variant: 'heading' },
      },
      {
        /* Matched on demand band and content type, not revenue rank. Ranking
           the catalogue by revenue puts soap operas beside a sci-fi flagship —
           true figures, useless benchmark. Both tokens are derived from the
           subject's own metadata, so the block waits for them rather than
           briefly rendering the whole catalogue under this heading. */
        id: 'tv-comps',
        type: 'table',
        title: 'Comparable titles',
        subtitle: 'Titles in the same demand band',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            demand: '{{demand}}',
            contentType: '{{contentType}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '12',
          },
        },
        options: { limit: 12 },
      },
      {
        id: 'tv-read-comps',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            demand: '{{demand}}',
            contentType: '{{contentType}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '12',
          },
        },
        options: { insight: 'comps' },
      },
    ],
  },

  {
    id: 'platform-fit',
    name: 'Platform Fit',
    description:
      'Which platform suits a title best? Its dollar contribution on each carrier, ranked, with the acquisition-versus-retention profile behind the ranking.',
    category: 'Content Valuation',
    inputs: [
      { key: 'titleId', label: 'Title', type: 'title', required: true, placeholder: 'Search for a title…' },
    ],
    defaultScope: { region: '', timePeriod: 'all' },
    /* The question is "which carrier", so platform spread leads and geography
       closes — the reverse of Title Valuation, which is about the asset rather
       than about where to place it. */
    blocks: [
      {
        id: 'pf-banner',
        type: 'banner',
        title: 'Subject',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}' },
      },

      // ─── Where it earns ──────────────────────────────────────────────────
      {
        id: 'pf-h-value',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Where the title earns',
        options: { variant: 'heading' },
      },
      {
        id: 'pf-kpis',
        type: 'kpis',
        title: 'Headline figures',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { rankSource: '/streaming-economics/titles/{{titleId}}/rankings' },
      },
      {
        id: 'pf-platforms',
        type: 'chart',
        title: 'Contribution by platform',
        subtitle: 'Ranked by revenue on each carrier',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { chartType: 'bar', groupBy: 'platform' },
      },
      {
        id: 'pf-read-platforms',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { insight: 'platforms' },
      },

      // ─── What the title does ─────────────────────────────────────────────
      {
        id: 'pf-h-role',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'What the title does for a carrier',
        options: { variant: 'heading' },
      },
      {
        id: 'pf-split',
        type: 'split',
        title: 'Acquisition vs retention',
        subtitle: 'Wins subscribers, or keeps the ones already there',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
      },
      {
        id: 'pf-read-role',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { insight: 'role' },
      },

      // ─── Reach ───────────────────────────────────────────────────────────
      {
        id: 'pf-h-geo',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'How far it travels',
        options: { variant: 'heading' },
      },
      {
        id: 'pf-regions',
        type: 'chart',
        title: 'Revenue by region',
        subtitle: 'UCAN, EMEA, APAC and LATAM',
        layout: 'half',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { chartType: 'donut', groupBy: 'region' },
      },
      {
        id: 'pf-read-geo',
        type: 'note',
        title: 'The read',
        layout: 'half',
        source: { endpoint: '/streaming-economics/titles/{{titleId}}/revenue' },
        options: { insight: 'geography' },
      },
    ],
  },

  {
    id: 'franchise-assessment',
    name: 'Franchise Assessment',
    description:
      'What is a franchise worth as one asset? Every title bearing the name, valued together — which instalment carries it, and where it earns.',
    category: 'Content Valuation',
    inputs: [
      {
        key: 'search',
        label: 'Franchise',
        type: 'search',
        required: true,
        placeholder: 'e.g. Harry Potter',
      },
    ],
    defaultScope: { region: '', timePeriod: 'all' },
    /* No banner: a franchise is a text match across many titles, not a single
       subject with a poster and a demand band, so there is nothing for a cover
       to identify. The heading carries the section instead. */
    blocks: [
      {
        id: 'fr-h-value',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'The franchise as one asset',
        options: { variant: 'heading' },
      },
      {
        id: 'fr-kpis',
        type: 'kpis',
        title: 'Franchise at a glance',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '200',
          },
        },
      },
      {
        id: 'fr-read-value',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '200',
          },
        },
        options: { insight: 'summary' },
      },

      // ─── The instalments ─────────────────────────────────────────────────
      {
        id: 'fr-h-titles',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Which instalment carries it',
        options: { variant: 'heading' },
      },
      {
        id: 'fr-table',
        type: 'table',
        title: 'Titles in the franchise',
        subtitle: 'Ranked by revenue contribution',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '20',
          },
        },
        options: { limit: 20 },
      },
      {
        /* Concentration is the franchise question: one hit with a long tail is
           a different asset from an evenly-earning series, and it decides
           whether the next instalment is worth commissioning. */
        id: 'fr-read-conc',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '200',
          },
        },
        options: { insight: 'concentration' },
      },

      // ─── Where it earns ──────────────────────────────────────────────────
      {
        id: 'fr-h-geo',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Where the franchise earns',
        options: { variant: 'heading' },
      },
      {
        id: 'fr-platforms',
        type: 'chart',
        title: 'Value by platform',
        subtitle: 'Which carriers hold the franchise',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '200',
          },
        },
        options: { chartType: 'bar', groupBy: 'platform' },
      },
      {
        id: 'fr-regions',
        type: 'chart',
        title: 'Value by region',
        subtitle: 'How far the IP travels',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '200',
          },
        },
        options: { chartType: 'donut', groupBy: 'region' },
      },
      {
        id: 'fr-read-platforms',
        type: 'note',
        title: 'The read',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '200',
          },
        },
        options: { insight: 'platforms' },
      },
      {
        id: 'fr-read-geo',
        type: 'note',
        title: 'The read',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '200',
          },
        },
        options: { insight: 'geography' },
      },

      // ─── What it does ────────────────────────────────────────────────────
      {
        id: 'fr-h-role',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'What the franchise does',
        options: { variant: 'heading' },
      },
      {
        id: 'fr-split',
        type: 'split',
        title: 'Acquisition vs retention',
        subtitle: 'Does the IP win subscribers, or hold them?',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '200',
          },
        },
      },
      {
        id: 'fr-read-role',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: {
            search: '{{search}}',
            region: '{{region}}',
            timePeriod: '{{timePeriod}}',
            limit: '200',
          },
        },
        options: { insight: 'role' },
      },
    ],
  },

  {
    id: 'catalogue-review',
    name: 'Catalogue Review',
    description:
      "What is carrying a platform's catalogue? Its highest-earning titles, the genre mix behind them, and how far revenue concentrates at the top.",
    category: 'Content Valuation',
    inputs: [
      { key: 'platform', label: 'Platform', type: 'platform', required: true, placeholder: 'Choose a platform…' },
    ],
    defaultScope: { region: '', timePeriod: 'all' },
    blocks: [
      {
        id: 'cr-banner',
        type: 'banner',
        title: 'Subject',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}' },
      },

      // ─── At a glance ─────────────────────────────────────────────────────
      {
        id: 'cr-h-value',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Catalogue at a glance',
        options: { variant: 'heading' },
      },
      {
        id: 'cr-kpis',
        type: 'kpis',
        title: 'Headline figures',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '100' },
        },
      },
      {
        id: 'cr-read-value',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '100' },
        },
        options: { insight: 'summary' },
      },

      // ─── What carries it ─────────────────────────────────────────────────
      {
        id: 'cr-h-top',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'What carries the catalogue',
        options: { variant: 'heading' },
      },
      {
        id: 'cr-table',
        type: 'table',
        title: 'Highest-earning titles',
        subtitle: 'Ranked by revenue contribution',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '10' },
        },
        options: { limit: 10 },
      },
      {
        id: 'cr-read-conc',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '100' },
        },
        options: { insight: 'concentration' },
      },

      // ─── The mix ─────────────────────────────────────────────────────────
      {
        id: 'cr-h-mix',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'The mix behind the money',
        options: { variant: 'heading' },
      },
      {
        id: 'cr-genres',
        type: 'chart',
        title: 'Genre mix',
        subtitle: 'Revenue by primary genre across the top 100 titles',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '100' },
        },
        options: { chartType: 'donut', groupBy: 'genre' },
      },
      {
        id: 'cr-regions',
        type: 'chart',
        title: 'Where the catalogue earns',
        subtitle: 'Revenue by region across the top 100 titles',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '100' },
        },
        options: { chartType: 'donut', groupBy: 'region' },
      },
      {
        id: 'cr-read-genres',
        type: 'note',
        title: 'The read',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '100' },
        },
        options: { insight: 'genres' },
      },
      {
        id: 'cr-read-geo',
        type: 'note',
        title: 'The read',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '100' },
        },
        options: { insight: 'geography' },
      },

      // ─── What it does ────────────────────────────────────────────────────
      {
        id: 'cr-h-role',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'What the catalogue does',
        options: { variant: 'heading' },
      },
      {
        id: 'cr-split',
        type: 'split',
        title: 'Acquisition vs retention',
        subtitle: 'Is this a catalogue that wins subscribers, or holds them?',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '100' },
        },
      },
      {
        id: 'cr-read-role',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '100' },
        },
        options: { insight: 'role' },
      },
    ],
  },

  {
    id: 'genre-performance',
    name: 'Genre Performance',
    description:
      'Which genres actually earn? Revenue split by genre across a platform, with the top titles driving each one.',
    category: 'Content Valuation',
    inputs: [
      { key: 'platform', label: 'Platform', type: 'platform', required: true, placeholder: 'Choose a platform…' },
    ],
    defaultScope: { region: '', timePeriod: 'all' },
    blocks: [
      {
        id: 'gp-banner',
        type: 'banner',
        title: 'Subject',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}' },
      },

      // ─── The mix ─────────────────────────────────────────────────────────
      {
        id: 'gp-h-mix',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'What the genre mix rewards',
        options: { variant: 'heading' },
      },
      {
        id: 'gp-genres',
        type: 'chart',
        title: 'Revenue by genre',
        subtitle: 'Across the top 200 earning titles',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '200' },
        },
        options: { chartType: 'donut', groupBy: 'genre' },
      },
      {
        id: 'gp-read-genres',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '200' },
        },
        options: { insight: 'genres' },
      },

      // ─── What drives it ──────────────────────────────────────────────────
      {
        id: 'gp-h-titles',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'The titles behind the mix',
        options: { variant: 'heading' },
      },
      {
        id: 'gp-kpis',
        type: 'kpis',
        title: 'Catalogue totals',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '200' },
        },
      },
      {
        id: 'gp-table',
        type: 'table',
        title: 'Top earners',
        subtitle: 'The titles behind the genre mix',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '15' },
        },
        options: { limit: 15 },
      },
      {
        id: 'gp-read-conc',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '200' },
        },
        options: { insight: 'concentration' },
      },

      // ─── Where genres travel ─────────────────────────────────────────────
      {
        id: 'gp-h-geo',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Where those genres travel',
        options: { variant: 'heading' },
      },
      {
        id: 'gp-regions',
        type: 'chart',
        title: 'Revenue by region',
        subtitle: 'Across the top 200 earning titles',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '200' },
        },
        options: { chartType: 'donut', groupBy: 'region' },
      },
      {
        id: 'gp-read-geo',
        type: 'note',
        title: 'The read',
        layout: 'half',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '200' },
        },
        options: { insight: 'geography' },
      },

      // ─── What it does ────────────────────────────────────────────────────
      {
        id: 'gp-h-role',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Acquisition or retention',
        options: { variant: 'heading' },
      },
      {
        id: 'gp-split',
        type: 'split',
        title: 'Acquisition vs retention',
        subtitle: 'What this genre mix does for the platform',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '200' },
        },
      },
      {
        id: 'gp-read-role',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: {
          endpoint: '/streaming-economics/leaderboard',
          params: { platform: '{{platform}}', region: '{{region}}', timePeriod: '{{timePeriod}}', limit: '200' },
        },
        options: { insight: 'role' },
      },
    ],
  },

  // ─── Streaming Metrics ───────────────────────────────────────────────────

  {
    id: 'platform-scorecard',
    name: 'Platform Scorecard',
    description:
      'How is one streamer performing? Subscribers, ARPU, revenue and churn for the quarter, with the subscriber trend behind them.',
    category: 'Streaming Metrics',
    inputs: [
      { key: 'platform', label: 'Platform', type: 'platform', required: true, placeholder: 'Choose a platform…' },
    ],
    defaultScope: { region: '', timePeriod: 'all' },
    /* Platform-shaped throughout: every block carries `shape: 'platform'` so
       the renderer reads subscribers/ARPU/churn rather than looking for a
       revenue contribution that these rows never had. */
    blocks: [
      {
        id: 'ps-banner',
        type: 'banner',
        title: 'Subject',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}' },
      },

      // ─── This quarter ────────────────────────────────────────────────────
      {
        id: 'ps-h-quarter',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Where it stands this quarter',
        options: { variant: 'heading' },
      },
      {
        id: 'ps-kpis',
        type: 'kpis',
        title: 'This quarter',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', platform: '{{platform}}' },
      },
      {
        /* Market context rather than a self-report: a scorecard that never
           says who else is in the market can't tell you whether the numbers
           above are good. */
        id: 'ps-read-market',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', insight: 'market' },
      },

      // ─── Trajectory ──────────────────────────────────────────────────────
      {
        id: 'ps-h-traj',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'The trajectory behind it',
        options: { variant: 'heading' },
      },
      {
        id: 'ps-trend',
        type: 'chart',
        title: 'Subscribers over time',
        subtitle: 'Every quarter on record',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'line', metric: 'subscribers' },
      },
      {
        id: 'ps-revenue',
        type: 'chart',
        title: 'Revenue over time',
        subtitle: 'Subscription revenue by quarter',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'line', metric: 'revenue' },
      },
      {
        id: 'ps-read-growth',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', insight: 'growth' },
      },

      // ─── Footprint ───────────────────────────────────────────────────────
      {
        id: 'ps-h-geo',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Where the base sits',
        options: { variant: 'heading' },
      },
      {
        id: 'ps-regions',
        type: 'chart',
        title: 'Subscribers by region',
        subtitle: 'Where the base sits',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'donut', groupBy: 'region', metric: 'subscribers' },
      },
      {
        id: 'ps-rev-regions',
        type: 'chart',
        title: 'Revenue by region',
        subtitle: 'Where the money is earned',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'bar', groupBy: 'region', metric: 'revenue' },
      },
      {
        id: 'ps-read-footprint',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', insight: 'footprint' },
      },
    ],
  },

  {
    id: 'market-leaderboard',
    name: 'Market Leaderboard',
    description:
      'Who leads the market? Every streamer ranked by subscribers, ARPU, revenue and churn — including the ones that never disclose.',
    category: 'Streaming Metrics',
    inputs: [],
    defaultScope: { region: '', timePeriod: 'all' },
    /* No subject and so no banner — this document is about the market, not
       about one platform in it. */
    blocks: [
      {
        id: 'ml-h-market',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'The market, at a glance',
        options: { variant: 'heading' },
      },
      {
        id: 'ml-kpis',
        type: 'kpis',
        title: 'Market totals',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform' },
      },
      {
        id: 'ml-read-market',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', insight: 'market' },
      },

      // ─── Ranked ──────────────────────────────────────────────────────────
      {
        id: 'ml-h-ranked',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Every platform, ranked',
        options: { variant: 'heading' },
      },
      {
        id: 'ml-table',
        type: 'table',
        title: 'Platform leaderboard',
        subtitle: 'Ranked by subscribers',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', limit: 12 },
      },

      // ─── How it divides ──────────────────────────────────────────────────
      {
        id: 'ml-h-divide',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'How the market divides',
        options: { variant: 'heading' },
      },
      {
        id: 'ml-share',
        type: 'chart',
        title: 'Subscriber share',
        subtitle: 'How the market divides by base',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', chartType: 'donut', groupBy: 'platform', metric: 'subscribers' },
      },
      {
        id: 'ml-revenue',
        type: 'chart',
        title: 'Revenue share',
        subtitle: 'Subscription revenue by platform',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', chartType: 'donut', groupBy: 'platform', metric: 'revenue' },
      },

      // ─── Momentum ────────────────────────────────────────────────────────
      {
        id: 'ml-h-flow',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Who is still growing',
        options: { variant: 'heading' },
      },
      {
        id: 'ml-net',
        type: 'chart',
        title: 'Net additions',
        subtitle: 'Gross additions less churn, by platform',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', chartType: 'bar', groupBy: 'platform', metric: 'net_additions' },
      },
      {
        id: 'ml-read-churn',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', insight: 'churn' },
      },
    ],
  },

  {
    id: 'churn-analysis',
    name: 'Churn Analysis',
    description:
      'Who is leaking subscribers? Gross additions against churn for every platform, and what that leaves in net terms.',
    category: 'Streaming Metrics',
    inputs: [],
    defaultScope: { region: '', timePeriod: 'all' },
    blocks: [
      {
        id: 'ca-h-leak',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'What the market wins, and what it loses',
        options: { variant: 'heading' },
      },
      {
        id: 'ca-kpis',
        type: 'kpis',
        title: 'Market totals',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform' },
      },
      {
        id: 'ca-read-churn',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', insight: 'churn' },
      },

      // ─── Platform by platform ────────────────────────────────────────────
      {
        id: 'ca-h-platform',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Platform by platform',
        options: { variant: 'heading' },
      },
      {
        id: 'ca-table',
        type: 'table',
        title: 'Additions and churn',
        subtitle: 'Every platform, this quarter',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: {
          shape: 'platform',
          limit: 12,
          columns: ['gross_additions', 'churns', 'net_additions', 'churn_rate'],
        },
      },

      // ─── Where it concentrates ───────────────────────────────────────────
      {
        id: 'ca-h-conc',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Where the loss concentrates',
        options: { variant: 'heading' },
      },
      {
        id: 'ca-churns',
        type: 'chart',
        title: 'Churn by platform',
        subtitle: 'Subscribers lost this quarter',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', chartType: 'bar', groupBy: 'platform', metric: 'churns' },
      },
      {
        id: 'ca-net',
        type: 'chart',
        title: 'Net additions',
        subtitle: 'What the platform kept after churn',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', chartType: 'bar', groupBy: 'platform', metric: 'net_additions' },
      },

      // ─── Rate, not volume ────────────────────────────────────────────────
      {
        id: 'ca-h-rate',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Rate, not volume',
        options: { variant: 'heading' },
      },
      {
        /* Absolute churn tracks size — the biggest platform loses the most
           subscribers almost by definition. Rate is the comparable figure and
           the one that says which base is actually leaking. */
        id: 'ca-rate',
        type: 'chart',
        title: 'Churn rate by platform',
        subtitle: 'Share of the base lost, which size does not flatter',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', chartType: 'bar', groupBy: 'platform', metric: 'churn_rate' },
      },
      {
        id: 'ca-read-market',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', insight: 'market' },
      },
    ],
  },

  {
    id: 'growth-trajectory',
    name: 'Growth Trajectory',
    description:
      'Is a platform still growing? Gross additions, net additions and subscribers across every quarter on record.',
    category: 'Streaming Metrics',
    inputs: [
      { key: 'platform', label: 'Platform', type: 'platform', required: true, placeholder: 'Choose a platform…' },
    ],
    defaultScope: { region: '', timePeriod: 'all' },
    blocks: [
      {
        id: 'gt-banner',
        type: 'banner',
        title: 'Subject',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}' },
      },

      // ─── The trajectory ──────────────────────────────────────────────────
      {
        id: 'gt-h-traj',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'The trajectory',
        options: { variant: 'heading' },
      },
      {
        id: 'gt-subs',
        type: 'chart',
        title: 'Subscribers over time',
        subtitle: 'Total base by quarter',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'area', metric: 'subscribers' },
      },
      {
        id: 'gt-read-growth',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', insight: 'growth' },
      },

      // ─── What drives it ──────────────────────────────────────────────────
      {
        id: 'gt-h-drivers',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'What drives the line',
        options: { variant: 'heading' },
      },
      {
        id: 'gt-adds',
        type: 'chart',
        title: 'Gross additions over time',
        subtitle: 'New subscribers won each quarter',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'line', metric: 'gross_additions' },
      },
      {
        id: 'gt-net',
        type: 'chart',
        title: 'Net additions over time',
        subtitle: 'What survived churn each quarter',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'line', metric: 'net_additions' },
      },

      // ─── Value per subscriber ────────────────────────────────────────────
      {
        id: 'gt-h-value',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Value per subscriber',
        options: { variant: 'heading' },
      },
      {
        id: 'gt-arpu',
        type: 'chart',
        title: 'ARPU over time',
        subtitle: 'Average revenue per user by quarter',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'line', metric: 'arpu' },
      },
      {
        id: 'gt-revenue',
        type: 'chart',
        title: 'Revenue over time',
        subtitle: 'Subscription revenue by quarter',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'line', metric: 'revenue' },
      },

      // ─── Latest quarter ──────────────────────────────────────────────────
      {
        id: 'gt-h-latest',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Where that leaves it',
        options: { variant: 'heading' },
      },
      {
        id: 'gt-kpis',
        type: 'kpis',
        title: 'Latest quarter',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', platform: '{{platform}}' },
      },
      {
        id: 'gt-read-footprint',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', insight: 'footprint' },
      },
    ],
  },

  {
    id: 'regional-breakdown',
    name: 'Regional Breakdown',
    description:
      'How does one streamer perform market by market? Subscribers, revenue and ARPU split across every region it operates in.',
    category: 'Streaming Metrics',
    inputs: [
      { key: 'platform', label: 'Platform', type: 'platform', required: true, placeholder: 'Choose a platform…' },
    ],
    defaultScope: { region: '', timePeriod: 'all' },
    blocks: [
      {
        id: 'rb-banner',
        type: 'banner',
        title: 'Subject',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}' },
      },

      // ─── Where the base sits ─────────────────────────────────────────────
      {
        id: 'rb-h-base',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'Where the base sits',
        options: { variant: 'heading' },
      },
      {
        id: 'rb-subs',
        type: 'chart',
        title: 'Subscribers by region',
        subtitle: 'How the base distributes',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'donut', groupBy: 'region', metric: 'subscribers' },
      },
      {
        id: 'rb-revenue',
        type: 'chart',
        title: 'Revenue by region',
        subtitle: 'Where the money is earned',
        layout: 'half',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'donut', groupBy: 'region', metric: 'revenue' },
      },
      {
        id: 'rb-read-footprint',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', insight: 'footprint' },
      },

      // ─── Value per market ────────────────────────────────────────────────
      {
        id: 'rb-h-value',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'What each market is worth',
        options: { variant: 'heading' },
      },
      {
        /* ARPU rather than revenue: a region can hold a large base and still
           be the least valuable per subscriber, and that gap is the whole
           point of splitting a platform by market. */
        id: 'rb-arpu',
        type: 'chart',
        title: 'ARPU by region',
        subtitle: 'Average revenue per user, market by market',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'bar', groupBy: 'region', metric: 'arpu' },
      },

      // ─── Over time ───────────────────────────────────────────────────────
      {
        id: 'rb-h-time',
        type: 'text',
        title: 'Section',
        layout: 'full',
        content: 'How it has moved',
        options: { variant: 'heading' },
      },
      {
        id: 'rb-trend',
        type: 'chart',
        title: 'Revenue over time',
        subtitle: 'Every quarter on record',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', chartType: 'area', metric: 'revenue' },
      },
      {
        id: 'rb-read-growth',
        type: 'note',
        title: 'The read',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/{{platform}}/history' },
        options: { shape: 'platform', insight: 'growth' },
      },
      {
        id: 'rb-kpis',
        type: 'kpis',
        title: 'Latest quarter',
        layout: 'full',
        source: { endpoint: '/streaming-economics/platforms/leaderboard', params: { region: '{{region}}' } },
        options: { shape: 'platform', platform: '{{platform}}' },
      },
    ],
  },
];

export function findTemplate(id: string | undefined): WorkflowTemplate | undefined {
  return WORKFLOW_TEMPLATES.find((t) => t.id === id);
}

/** Templates grouped by product line, preserving declaration order. */
export function templatesByCategory(): { category: string; templates: WorkflowTemplate[] }[] {
  const order: string[] = [];
  const groups = new Map<string, WorkflowTemplate[]>();
  for (const template of WORKFLOW_TEMPLATES) {
    if (!groups.has(template.category)) {
      groups.set(template.category, []);
      order.push(template.category);
    }
    groups.get(template.category)?.push(template);
  }
  return order.map((category) => ({ category, templates: groups.get(category) ?? [] }));
}
